import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FileText, Eye, RefreshCw, AlertTriangle, Upload, Download, CheckCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface ReviewTemplate {
  id: string;
  filename: string;
  created_at: string;
  user_id: string;
  admin_approval_status: string;
  admin_uploaded_html?: string;
  file_path: string;
  processing_status: string;
  processed_html?: string;
}

export default function ReviewQueue() {
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ReviewTemplate | null>(null);
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [addToLibrary, setAddToLibrary] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchPendingTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Fetching pending templates directly from database...");
      
      // First, let's check all templates to see what's in the database
      const { data: allTemplates } = await supabase
        .from('templates')
        .select('id, filename, needs_review, processing_status, admin_approval_status')
        .order('created_at', { ascending: false });
      
      console.log("All templates in database:", allTemplates);
      
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('needs_review', true)
        .order('created_at', { ascending: false });

      console.log("Pending templates query result:", { data, error });

      if (error) {
        throw error;
      }

      setTemplates(data || []);
      console.log("Set templates:", data?.length || 0, "templates");
    } catch (error) {
      console.error("Error fetching pending templates:", error);
      setError(error instanceof Error ? error.message : "Failed to load pending templates");
      toast({
        title: "Error",
        description: "Failed to load pending templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingTemplates();
  }, []);

  const handleDownloadTemplate = async (template: ReviewTemplate) => {
    try {
      const { data, error } = await supabase.storage
        .from('templates')
        .download(template.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    }
  };

  const handleUploadHTML = async () => {
    if (!htmlFile || !selectedTemplate) return;

    setUploading(true);
    try {
      const fileContent = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove data:text/html;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(htmlFile);
      });

      const { data, error } = await supabase.functions.invoke('admin-approval', {
        body: {
          action: 'upload-html',
          templateId: selectedTemplate.id,
          htmlFile: {
            content: fileContent,
            name: htmlFile.name
          }
        }
      });

      if (error || data.error) {
        throw new Error(data?.error || error?.message || 'Upload failed');
      }

      toast({
        title: "Success",
        description: "HTML file uploaded successfully",
      });

      setHtmlFile(null);
      fetchPendingTemplates(); // Refresh the list
    } catch (error) {
      console.error('Error uploading HTML:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to upload HTML file",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedTemplate) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('templates')
        .update({ 
          needs_review: false,
          is_approved: true,
          admin_approval_status: 'approved',
          is_company_library: !!addToLibrary,
          admin_reviewed_at: new Date().toISOString()
        })
        .eq('id', selectedTemplate.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Template approved successfully",
      });

      setSelectedTemplate(null);
      setAddToLibrary(false);
      fetchPendingTemplates();
    } catch (error) {
      console.error('Error approving template:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to approve template",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTemplate) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('templates')
        .update({ 
          needs_review: false,
          is_approved: false,
          admin_approval_status: 'rejected',
          rejection_reason: rejectionReason,
          admin_reviewed_at: new Date().toISOString()
        })
        .eq('id', selectedTemplate.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Template rejected successfully",
      });

      setSelectedTemplate(null);
      setRejectionReason("");
      fetchPendingTemplates();
    } catch (error) {
      console.error('Error rejecting template:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to reject template",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Review Queue</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchPendingTemplates}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin")}>
            Back to Dashboard
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Templates Awaiting Review</CardTitle>
          <CardDescription>
            Download templates, upload HTML files, and approve or reject submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse py-8 text-center">
              Loading review queue...
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No templates waiting for review.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-gray-500" />
                      {template.filename.replace(/\.[^/.]+$/, "")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {template.admin_uploaded_html ? 'HTML Uploaded' : 'Pending HTML'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(template.created_at), "MMM d, yyyy h:mm a")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadTemplate(template)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/review/${template.id}`)}
                          className="flex items-center gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Review
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
